import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Activity, Clock3, Dumbbell, Flame, LoaderCircle, MapPin, PersonStanding, ShieldCheck, Sparkles, Waves } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { createPaygCheckoutSession, getPublicPaygClasses } from "../services/billing";

type PublicClass = Awaited<ReturnType<typeof getPublicPaygClasses>>["data"]["classes"][number];

function toDateValue(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === "object") {
    const maybeTimestamp = value as {
      toDate?: () => Date;
      seconds?: number;
      _seconds?: number;
      nanoseconds?: number;
      _nanoseconds?: number;
    };

    if (typeof maybeTimestamp.toDate === "function") {
      const date = maybeTimestamp.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const seconds =
      typeof maybeTimestamp.seconds === "number"
        ? maybeTimestamp.seconds
        : typeof maybeTimestamp._seconds === "number"
        ? maybeTimestamp._seconds
        : null;

    if (seconds != null) {
      const nanos =
        typeof maybeTimestamp.nanoseconds === "number"
          ? maybeTimestamp.nanoseconds
          : typeof maybeTimestamp._nanoseconds === "number"
          ? maybeTimestamp._nanoseconds
          : 0;
      const date = new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function formatDateTime(ts: unknown, timeZone: string) {
  const date = toDateValue(ts);
  if (!date) return "Time TBC";

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(date);
}

function formatDayKey(ts: unknown, timeZone: string) {
  const date = toDateValue(ts);
  if (!date) return "Upcoming";

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone,
  }).format(date);
}

function formatDayLabel(ts: unknown, timeZone: string) {
  const date = toDateValue(ts);
  if (!date) return "TBC";

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone,
  }).format(date);
}

function formatTime(ts: unknown, timeZone: string) {
  const date = toDateValue(ts);
  if (!date) return "TBC";

  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(date);
}

function formatMoney(minor: number) {
  return `GBP ${(minor / 100).toFixed(2)}`;
}

function classMeta(title?: string) {
  const t = (title || "").toLowerCase();

  if (t.includes("hyrox")) {
    return {
      label: "HYROX",
      stripe: "bg-[linear-gradient(180deg,rgba(96,165,250,0.95),rgba(59,84,170,0.9))]",
      pill: "border-[color:rgba(136,156,255,0.24)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]",
      iconWrap: "border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.16)] text-[color:#dbe6ff]",
      Icon: Flame,
    };
  }

  if (t.includes("strength")) {
    return {
      label: "STRENGTH",
      stripe: "bg-[linear-gradient(180deg,rgba(59,130,246,0.95),rgba(14,116,144,0.9))]",
      pill: "border-sky-500/24 bg-sky-500/12 text-sky-200",
      iconWrap: "border-sky-500/20 bg-sky-500/12 text-sky-200",
      Icon: Dumbbell,
    };
  }

  if (t.includes("yoga")) {
    return {
      label: "YOGA",
      stripe: "bg-[linear-gradient(180deg,rgba(45,212,191,0.95),rgba(13,148,136,0.9))]",
      pill: "border-teal-500/24 bg-teal-500/12 text-teal-200",
      iconWrap: "border-teal-500/20 bg-teal-500/12 text-teal-200",
      Icon: PersonStanding,
    };
  }

  if (t.includes("run club")) {
    return {
      label: "RUN CLUB",
      stripe: "bg-[linear-gradient(180deg,rgba(125,211,252,0.95),rgba(59,130,246,0.9))]",
      pill: "border-cyan-400/24 bg-cyan-400/12 text-cyan-200",
      iconWrap: "border-cyan-400/20 bg-cyan-400/12 text-cyan-200",
      Icon: Waves,
    };
  }

  return {
    label: (title || "SESSION").toUpperCase(),
    stripe: "bg-[linear-gradient(180deg,rgba(148,163,184,0.85),rgba(71,85,105,0.8))]",
    pill: "border-white/12 bg-white/[0.05] text-white/80",
    iconWrap: "border-white/12 bg-white/[0.05] text-white/80",
    Icon: Activity,
  };
}

function capacityPercent(bookedCount: number, capacity: number) {
  if (!capacity || capacity <= 0) return 0;
  return Math.max(0, Math.min(100, (bookedCount / capacity) * 100));
}

export default function PayAsYouGo() {
  const { user, appUser } = useAuth();
  const [classes, setClasses] = useState<PublicClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [paygBusyId, setPaygBusyId] = useState<string | null>(null);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestDateOfBirth, setGuestDateOfBirth] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const classesRes = await getPublicPaygClasses({});
        if (!alive) return;
        setClasses(classesRes.data.classes);
      } catch (err: any) {
        if (alive) {
          setError(err?.message || "Failed to load PAYG classes.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!guestName && appUser?.name) {
      setGuestName(appUser.name);
    }
  }, [appUser?.name, guestName]);

  useEffect(() => {
    if (!guestEmail && user?.email) {
      setGuestEmail(user.email);
    }
  }, [guestEmail, user?.email]);

  async function handlePaygCheckout(selectedClass: PublicClass) {
    if (!guestName.trim() || !guestDateOfBirth.trim() || !guestEmail.trim() || !guestPhone.trim()) {
      setError("Add your full guest details before paying and booking.");
      return;
    }

    try {
      setPaygBusyId(selectedClass.id);
      setError(null);
      const result = await createPaygCheckoutSession({
        classId: selectedClass.id,
        guestName: guestName.trim(),
        guestDateOfBirth: guestDateOfBirth.trim(),
        guestEmail: guestEmail.trim(),
        guestPhone: guestPhone.trim(),
        origin: window.location.origin,
      });

      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err: any) {
      setError(err?.message || "Failed to start PAYG checkout.");
    } finally {
      setPaygBusyId(null);
    }
  }

  const groupedClasses = useMemo(() => {
    const groups: Array<{
      key: string;
      dayLabel: string;
      shortLabel: string;
      items: PublicClass[];
    }> = [];
    const map = new Map<string, number>();

    for (const item of classes) {
      const key = formatDayKey(item.startTime, item.timezone);

      if (!map.has(key)) {
        map.set(key, groups.length);
        groups.push({
          key,
          dayLabel: key,
          shortLabel: formatDayLabel(item.startTime, item.timezone),
          items: [],
        });
      }

      groups[map.get(key)!].items.push(item);
    }

    return groups;
  }, [classes]);

  const selectedGroup = groupedClasses.find((group) => group.key === selectedDayKey) ?? groupedClasses[0] ?? null;

  const weekLabel = useMemo(() => {
    if (!classes.length) return null;
    const first = toDateValue(classes[0].startTime);
    const last = toDateValue(classes[classes.length - 1].startTime);
    if (!first || !last) return null;
    const from = first.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const to = last.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    return `${from} – ${to}`;
  }, [classes]);

  useEffect(() => {
    if (!groupedClasses.length) {
      setSelectedDayKey(null);
      return;
    }

    setSelectedDayKey((current) => {
      if (current && groupedClasses.some((group) => group.key === current)) {
        return current;
      }
      return groupedClasses[0].key;
    });
  }, [groupedClasses]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {loading ? (
          <div className="rounded-3xl border border-white/10 bg-neutral-950 p-6 text-neutral-400">
            Loading PAYG classes...
          </div>
        ) : (
          <div className="space-y-6">
            <section className="rounded-[1.75rem] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.14),transparent_32%),linear-gradient(180deg,rgba(12,14,22,0.98),rgba(8,9,14,0.98))] p-5 sm:p-6">
              <div className="flex flex-col gap-5 border-b border-white/8 pb-5 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-[color:rgba(136,156,255,0.18)] bg-[color:rgba(59,84,170,0.18)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[color:#dbe6ff]">
                    <Sparkles className="h-3.5 w-3.5" />
                    PAYG Schedule
                  </div>
                  <h1 className="mt-4 text-3xl font-heading tracking-tight text-white sm:text-4xl">
                    Pick your day, then pay and book.
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/56 sm:text-base">
                    Browse one-off sessions in the same weekly view as the member schedule.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em]">
                  {weekLabel ? (
                    <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-white/62">
                      {weekLabel}
                    </span>
                  ) : null}
                  <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-emerald-200">
                    {classes.length} PAYG session{classes.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <label className="block">
                  <div className="mb-2 text-sm font-medium text-white/72">Your name</div>
                  <input
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Jordan Example"
                    required
                    className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-[color:rgba(136,156,255,0.30)] focus:bg-white/[0.06]"
                  />
                </label>

                <label className="block">
                  <div className="mb-2 text-sm font-medium text-white/72">Date of birth</div>
                  <input
                    type="date"
                    value={guestDateOfBirth}
                    onChange={(e) => setGuestDateOfBirth(e.target.value)}
                    required
                    className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-[color:rgba(136,156,255,0.30)] focus:bg-white/[0.06]"
                  />
                </label>

                <label className="block">
                  <div className="mb-2 text-sm font-medium text-white/72">Email address</div>
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-[color:rgba(136,156,255,0.30)] focus:bg-white/[0.06]"
                  />
                </label>

                <label className="block lg:col-span-2">
                  <div className="mb-2 text-sm font-medium text-white/72">Contact number</div>
                  <input
                    type="tel"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder="07700 900123"
                    required
                    className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-[color:rgba(136,156,255,0.30)] focus:bg-white/[0.06]"
                  />
                </label>
              </div>

              <div className="mt-5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex gap-2.5">
                  {groupedClasses.map((group) => {
                    const isSelected = group.key === selectedGroup?.key;

                    return (
                      <button
                        key={`payg-nav-${group.key}`}
                        type="button"
                        onClick={() => setSelectedDayKey(group.key)}
                        className={[
                          "min-w-[110px] rounded-full border px-4 py-3 text-left transition",
                          isSelected
                            ? "border-[color:rgba(136,156,255,0.24)] bg-[linear-gradient(180deg,rgba(61,88,177,0.34),rgba(33,48,99,0.28))] text-[color:#eef3ff] shadow-[0_8px_24px_rgba(41,67,155,0.24)]"
                            : "border-white/10 bg-white/[0.03] text-white/68 hover:border-[color:rgba(136,156,255,0.18)] hover:bg-[color:rgba(59,84,170,0.12)] hover:text-white/84",
                        ].join(" ")}
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                          {group.shortLabel}
                        </div>
                        <div className="mt-1 text-sm font-semibold">
                          {group.items.length} class{group.items.length === 1 ? "" : "es"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {classes.length === 0 ? (
              <div className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,18,0.98),rgba(8,9,12,0.98))] p-6 text-white/70">
                No PAYG classes are available right now.
              </div>
            ) : selectedGroup ? (
              <section className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,18,0.98),rgba(8,9,12,0.98))] p-5 sm:p-6">
                <div className="flex flex-col gap-2 border-b border-white/8 pb-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:#aabaff]">
                      {selectedGroup.shortLabel}
                    </div>
                    <h2 className="mt-2 text-2xl font-heading tracking-tight text-white">
                      {selectedGroup.dayLabel}
                    </h2>
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] text-white/36">
                    {selectedGroup.items.length} session{selectedGroup.items.length === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="mt-4 grid gap-3">
                  {selectedGroup.items.map((item) => {
                    const meta = classMeta(item.title);
                    const Icon = meta.Icon;
                    const percent = capacityPercent(item.bookedCount, item.capacity);
                    const spotsLeft = Math.max(0, item.capacity - item.bookedCount);
                    const isBusy = paygBusyId === item.id;

                    return (
                      <div
                        key={item.id}
                        className="relative overflow-hidden rounded-[1.35rem] border border-white/10 bg-[linear-gradient(180deg,rgba(17,21,34,0.84),rgba(9,11,18,0.72))] p-3.5 shadow-[0_10px_28px_rgba(0,0,0,0.28)] transition hover:border-[color:rgba(136,156,255,0.18)] hover:bg-[linear-gradient(180deg,rgba(19,24,39,0.9),rgba(10,12,20,0.76))] sm:p-4"
                      >
                        <div className={`absolute inset-y-0 left-0 w-1 ${meta.stripe}`} />

                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div className="min-w-0 flex-1 pl-2">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[1rem] border ${meta.iconWrap}`}>
                                <Icon className="h-4.5 w-4.5" />
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-lg font-heading tracking-[0.1em] text-white">
                                    {meta.label}
                                  </div>
                                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${meta.pill}`}>
                                    {formatTime(item.startTime, item.timezone)}
                                    {item.endTime ? `–${formatTime(item.endTime, item.timezone)}` : ""}
                                  </span>
                                  <span className="inline-flex rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200">
                                    {formatMoney(item.paygPriceMinor)}
                                  </span>
                                  {spotsLeft === 0 ? (
                                    <span className="inline-flex rounded-full border border-red-500/20 bg-red-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-red-200">
                                      Sold out
                                    </span>
                                  ) : null}
                                </div>

                                <div className="mt-2.5 grid gap-2 text-[13px] text-white/60 sm:grid-cols-3">
                                  <div className="flex items-center gap-2">
                                    <Clock3 className="h-3.5 w-3.5 text-[color:#aabaff]" />
                                    <span>{formatDateTime(item.startTime, item.timezone)}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <ShieldCheck className="h-3.5 w-3.5 text-[color:#aabaff]" />
                                    <span>{item.coachName || "Coach TBC"}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <MapPin className="h-3.5 w-3.5 text-[color:#aabaff]" />
                                    <span>{item.location || "Locker Room"}</span>
                                  </div>
                                </div>

                                <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                                  <div>
                                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-white/34">
                                      <span>Capacity</span>
                                      <span>
                                        {item.bookedCount}/{item.capacity || "—"}
                                      </span>
                                    </div>
                                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-white/8">
                                      <div
                                        className="h-full rounded-full bg-[linear-gradient(90deg,rgba(96,165,250,0.95),rgba(59,84,170,0.95))]"
                                        style={{ width: `${percent}%` }}
                                      />
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.16em]">
                                    <span className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-white/68">
                                      {spotsLeft} spot{spotsLeft === 1 ? "" : "s"} left
                                    </span>
                                    <span className="inline-flex rounded-full border border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.18)] px-2.5 py-1 text-[color:#dbe6ff]">
                                      Public PAYG
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex w-full flex-col gap-2 lg:w-auto lg:min-w-[180px]">
                            <button
                              type="button"
                              onClick={() => handlePaygCheckout(item)}
                              disabled={isBusy || spotsLeft === 0}
                              className={[
                                "inline-flex items-center justify-center gap-2 rounded-[1rem] border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40",
                                spotsLeft === 0
                                  ? "border-white/10 bg-white/[0.04] text-white/34"
                                  : "border-emerald-500/20 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15",
                              ].join(" ")}
                            >
                              {isBusy ? (
                                <LoaderCircle className="h-4 w-4 animate-spin" />
                              ) : (
                                <ArrowRight className="h-4 w-4" />
                              )}
                              {spotsLeft === 0 ? "Sold out" : `Pay & Book ${formatMoney(item.paygPriceMinor)}`}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>
        )}

        {error ? (
          <div className="mt-6 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
